import { Bus, TrainFront } from 'lucide-react';
import { locale, tx } from '../i18n';
import type { NearbyTransport, TransportStop } from '../types/api';
import ReportSection from './report/ReportSection';

interface NearbyTransportSectionProps {
  transport: NearbyTransport;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

function km(metres: number): string {
  return (metres / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 });
}

/**
 * Stations and bus stops near the address. Informational, like schools: how much a particular
 * line matters is the tenant's judgement, not something to score.
 */
export default function NearbyTransportSection({ transport, checkedAt }: NearbyTransportSectionProps) {
  const { stations, busStops } = transport;
  if (stations.length === 0 && busStops.length === 0) {
    return null;
  }

  return (
    <ReportSection
      id="section-transport"
      icon={TrainFront}
      title={tx('Getting around', 'Ulaşım',
    'Komunikacja',
    'Deslocações',
    'Transporte')}
      note={tx(
        `Stations within ${km(transport.stationRadiusMetres)} km, bus stops within ${km(transport.busRadiusMetres)} km.`,
        `${km(transport.stationRadiusMetres)} km içindeki istasyonlar, ${km(transport.busRadiusMetres)} km içindeki otobüs durakları.`,
        `Stacje w promieniu ${km(transport.stationRadiusMetres)} km, przystanki autobusowe w promieniu ${km(transport.busRadiusMetres)} km.`,
        `Estações até ${km(transport.stationRadiusMetres)} km, paragens de autocarro até ${km(transport.busRadiusMetres)} km.`,
        `Estaciones a menos de ${km(transport.stationRadiusMetres)} km, paradas de autobús a menos de ${km(transport.busRadiusMetres)} km.`,
      )}
      informational
      quality="verified"
      source={{
        name: tx(
          'NaPTAN, the national public transport access node register',
          'NaPTAN, ulusal toplu taşıma durak kaydı',
          'NaPTAN, krajowy rejestr przystanków transportu publicznego',
          'NaPTAN, o registo nacional de paragens de transporte público',
          'NaPTAN, el registro nacional de paradas de transporte público',
        ),
        version: transport.dataVersion,
        checked: checkedAt,
      }}
    >

      <div className="grid utilities-grid">
        <StopList
          icon={<TrainFront size={14} aria-hidden="true" />}
          title={tx('Stations', 'İstasyonlar',
    'Stacje',
    'Estações',
    'Estaciones')}
          stops={stations}
          empty={tx(
            `No station within ${km(transport.stationRadiusMetres)} km.`,
            `${km(transport.stationRadiusMetres)} km içinde istasyon yok.`,
            `Brak stacji w promieniu ${km(transport.stationRadiusMetres)} km.`,
            `Nenhuma estação até ${km(transport.stationRadiusMetres)} km.`,
            `Ninguna estación a menos de ${km(transport.stationRadiusMetres)} km.`,
          )}
        />
        <StopList
          icon={<Bus size={14} aria-hidden="true" />}
          title={tx('Bus stops', 'Otobüs durakları',
    'Przystanki autobusowe',
    'Paragens de autocarro',
    'Paradas de autobús')}
          stops={busStops}
          empty={tx(
            `No bus stop within ${km(transport.busRadiusMetres)} km.`,
            `${km(transport.busRadiusMetres)} km içinde otobüs durağı yok.`,
            `Brak przystanku autobusowego w promieniu ${km(transport.busRadiusMetres)} km.`,
            `Nenhuma paragem de autocarro até ${km(transport.busRadiusMetres)} km.`,
            `Ninguna parada de autobús a menos de ${km(transport.busRadiusMetres)} km.`,
          )}
        />
      </div>

      <p className="tiny muted">{transport.note}</p>
      <p className="tiny muted">
        {tx(
          'Source: NaPTAN, the national register of public transport access nodes (Department for Transport), Open Government Licence.',
          'Kaynak: NaPTAN, ulusal toplu taşıma durak kaydı (Ulaştırma Bakanlığı), Open Government Licence.',
    'Źródło: NaPTAN, krajowy rejestr punktów dostępu do transportu publicznego (Department for Transport), Open Government Licence.',
    'Fonte: NaPTAN, o registo nacional de pontos de acesso ao transporte público (Department for Transport), Open Government Licence.',
    'Fuente: NaPTAN, el registro nacional de puntos de acceso al transporte público (Department for Transport), Open Government Licence.',
        )}
      </p>
    </ReportSection>
  );
}

function StopList({
  icon,
  title,
  stops,
  empty,
}: {
  icon: React.ReactNode;
  title: string;
  stops: TransportStop[];
  empty: string;
}) {
  return (
    <div className="stack stack--tight">
      <h3 className="label row" style={{ gap: 6 }}>
        {icon}
        {title}
      </h3>
      {stops.length === 0 ? (
        <p className="small muted">{empty}</p>
      ) : (
        <ul className="stop-list">
          {stops.map((stop) => (
            <li key={`${stop.mode}-${stop.name}`} className="stop">
              <span className="stop__text">
                <span className="stop__name">{stop.name}</span>
                <span className="tiny muted">
                  {stop.modeLabel}
                  {stop.street ? ` · ${stop.street}` : ''}
                  {stop.stopCount > 1
                    ? tx(` · ${stop.stopCount} stops`, ` · ${stop.stopCount} durak`,
    ` · ${stop.stopCount} przystanki`,
    ` · ${stop.stopCount} paragens`,
    ` · ${stop.stopCount} paradas`)
                    : ''}
                </span>
              </span>
              <span className="stop__distance">
                {formatDistance(stop.distanceMetres)}
                <span className="muted">
                  {' '}
                  &middot; {tx(`~${stop.walkMinutes} min`, `~${stop.walkMinutes} dk`,
    `~${stop.walkMinutes} min`,
    `~${stop.walkMinutes} min`,
    `~${stop.walkMinutes} min`)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDistance(metres: number): string {
  return metres < 1000
    ? `${Math.round(metres / 10) * 10} m`
    : `${(metres / 1000).toLocaleString(locale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 })} km`;
}
